import type {
  ClearCardBankState,
  ExistingCashoutBankSelection,
  LowIncomeCacheData,
  ManualCashoutBankSelection,
  OutstandingLoanCacheItem,
  UnionLoanCacheData,
  UnionLoanCacheState,
  // byClaude-start 2026-08-28 10:36:26
  UnionLoanDebtSelectionKey,
  // byClaude-end 2026-08-28 10:36:26
  // byClaude-start 2026-08-31 17:49:47
  UnionLoanPendingDebtRecovery,
  // byClaude-end 2026-08-31 17:49:47
  UnionLoanProductType,
} from '@/interface/unionLoan';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type {
  CashoutBankSelection,
  ClearCardBankIdentity,
  ClearCardBankState,
  ExistingCashoutBankSelection,
  LowIncomeCacheData,
  ManualCashoutBankSelection,
  OutstandingLoanCacheItem,
  UnionLoanCacheData,
  UnionLoanCacheState,
  // byClaude-start 2026-08-28 10:36:26
  UnionLoanDebtSelectionKey,
  // byClaude-end 2026-08-28 10:36:26
  // byClaude-start 2026-08-31 17:49:47
  UnionLoanPendingDebtRecovery,
  // byClaude-end 2026-08-31 17:49:47
  UnionLoanProductType,
} from '@/interface/unionLoan';

const initialState: UnionLoanCacheState = {
  CL: {},
  KKC: {},
  CMC: {},
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const CACHE_KEYS = new Set([
  'cashoutAmount',
  'cashoutBankSelection',
  'loanMaturity',
  'divRepaymentTime',
  'selectedCouponId',
  'selectedDebtContractNos',
  // byClaude-start 2026-08-28 10:36:26
  'selectedDebtKeys',
  // byClaude-end 2026-08-28 10:36:26
  // byClaude-start 2026-08-31 17:49:47
  'pendingDebtRecovery',
  // byClaude-end 2026-08-31 17:49:47
  'clearCardBankStates',
  'clearCardDraftBankStates',
  'clearCardCompVisible',
  'lowIncomeData',
  'isCheckDcSwitch',
]);

const CLEAR_CARD_STABLE_STATE_KEYS = new Set(['matchBy', 'value', 'checked', 'bankLoanAmt']);
const CLEAR_CARD_BANK_AND_MASK_STATE_KEYS = new Set([
  'matchBy',
  'bankId',
  'bankCardNum1',
  'checked',
  'bankLoanAmt',
]);

const OUTSTANDING_LOAN_KEYS = new Set([
  'loanInstitutionCode',
  'loanInstitutionName',
  'loanType',
  'loanTypeName',
  'loanAmt',
  'remainingPeriods',
  'monthlyRepaymentAmt',
  'outstandingLoanAmt',
]);

const LOW_INCOME_DATA_KEYS = new Set(['outUnsecuredLoanFlag', 'outstandingLoanList']);
const EXISTING_BANK_SELECTION_KEYS = new Set(['source', 'matchBy', 'value']);
// byClaude-start 2026-08-28 10:36:26
const DEBT_SELECTION_KEY_KEYS = new Set(['productCode', 'orderId', 'contractNo']);
const DEBT_PRODUCT_CODES = new Set(['CL', 'KKC', 'CMC']);
// byClaude-end 2026-08-28 10:36:26
// byClaude-start 2026-08-31 17:49:47
const PENDING_DEBT_RECOVERY_KEYS = new Set([
  'orderId',
  'selectedDebtContractNos',
  'selectedDebtKeys',
]);
// byClaude-end 2026-08-31 17:49:47

const MANUAL_BANK_SELECTION_KEYS = new Set([
  'source',
  'bankCardCode',
  'bankCardName',
  'bankCardEnName',
  'bankCardNum',
]);

const isStrictCalendarDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const isExistingBankSelection = (value: unknown): value is ExistingCashoutBankSelection =>
  isRecord(value) &&
  !Object.keys(value).some((key) => !EXISTING_BANK_SELECTION_KEYS.has(key)) &&
  value.source === 'existing' &&
  (value.matchBy === 'id' || value.matchBy === 'bankCardNumEncrypt') &&
  typeof value.value === 'string' &&
  value.value.trim().length > 0;

const isManualBankSelection = (value: unknown): value is ManualCashoutBankSelection =>
  isRecord(value) &&
  !Object.keys(value).some((key) => !MANUAL_BANK_SELECTION_KEYS.has(key)) &&
  value.source === 'manual' &&
  typeof value.bankCardCode === 'string' &&
  value.bankCardCode.length > 0 &&
  typeof value.bankCardName === 'string' &&
  value.bankCardName.length > 0 &&
  typeof value.bankCardNum === 'string' &&
  value.bankCardNum.length > 0 &&
  (value.bankCardEnName === undefined || typeof value.bankCardEnName === 'string');

const hasOnlyKeys = (value: Record<string, unknown>, allowedKeys: Set<string>) =>
  !Object.keys(value).some((key) => !allowedKeys.has(key));

const isClearCardBankState = (value: unknown): value is ClearCardBankState => {
  if (
    !isRecord(value) ||
    typeof value.checked !== 'boolean' ||
    typeof value.bankLoanAmt !== 'number' ||
    !Number.isFinite(value.bankLoanAmt) ||
    value.bankLoanAmt < 0
  ) {
    return false;
  }

  if (
    value.matchBy === 'id' ||
    value.matchBy === 'bankCardNumEncrypt' ||
    value.matchBy === 'token'
  ) {
    return (
      hasOnlyKeys(value, CLEAR_CARD_STABLE_STATE_KEYS) &&
      typeof value.value === 'string' &&
      value.value.trim().length > 0
    );
  }

  return (
    value.matchBy === 'bankIdAndBankCardNum1' &&
    hasOnlyKeys(value, CLEAR_CARD_BANK_AND_MASK_STATE_KEYS) &&
    typeof value.bankId === 'string' &&
    value.bankId.trim().length > 0 &&
    typeof value.bankCardNum1 === 'string' &&
    value.bankCardNum1.trim().length > 0
  );
};

const isOutstandingLoanCacheItem = (value: unknown): value is OutstandingLoanCacheItem =>
  isRecord(value) &&
  hasOnlyKeys(value, OUTSTANDING_LOAN_KEYS) &&
  [...OUTSTANDING_LOAN_KEYS].every((key) => typeof value[key] === 'string');

const isLowIncomeCacheData = (value: unknown): value is LowIncomeCacheData =>
  isRecord(value) &&
  hasOnlyKeys(value, LOW_INCOME_DATA_KEYS) &&
  (value.outUnsecuredLoanFlag === 'Y' || value.outUnsecuredLoanFlag === 'N') &&
  Array.isArray(value.outstandingLoanList) &&
  value.outstandingLoanList.every(isOutstandingLoanCacheItem);

// byClaude-start 2026-08-28 10:36:26
/** 校验持久化组合键，避免未知产品或额外接口字段进入本地缓存 */
const isDebtSelectionKey = (value: unknown): value is UnionLoanDebtSelectionKey =>
  isRecord(value) &&
  hasOnlyKeys(value, DEBT_SELECTION_KEY_KEYS) &&
  typeof value.productCode === 'string' &&
  DEBT_PRODUCT_CODES.has(value.productCode) &&
  (value.orderId === undefined ||
    (typeof value.orderId === 'string' && value.orderId.trim().length > 0)) &&
  typeof value.contractNo === 'string' &&
  value.contractNo.trim().length > 0;
// byClaude-end 2026-08-28 10:36:26

// byClaude-start 2026-08-31 17:49:47
/** 校验只用于确认页返回的临时借新还旧选择 */
const isPendingDebtRecovery = (value: unknown): value is UnionLoanPendingDebtRecovery =>
  isRecord(value) &&
  hasOnlyKeys(value, PENDING_DEBT_RECOVERY_KEYS) &&
  typeof value.orderId === 'string' &&
  value.orderId.trim().length > 0 &&
  (value.selectedDebtContractNos === undefined ||
    (Array.isArray(value.selectedDebtContractNos) &&
      value.selectedDebtContractNos.every(
        (item) => typeof item === 'string' && item.trim().length > 0
      ))) &&
  (value.selectedDebtKeys === undefined ||
    (Array.isArray(value.selectedDebtKeys) && value.selectedDebtKeys.every(isDebtSelectionKey)));
// byClaude-end 2026-08-31 17:49:47

const isValidProductCache = (value: unknown): value is UnionLoanCacheData => {
  if (!isRecord(value) || Object.keys(value).some((key) => !CACHE_KEYS.has(key))) return false;
  return (
    (value.cashoutAmount === undefined ||
      (Number.isFinite(value.cashoutAmount) && (value.cashoutAmount as number) >= 0)) &&
    (value.cashoutBankSelection === undefined ||
      isExistingBankSelection(value.cashoutBankSelection) ||
      isManualBankSelection(value.cashoutBankSelection)) &&
    (value.loanMaturity === undefined ||
      (Number.isFinite(value.loanMaturity) && (value.loanMaturity as number) > 0)) &&
    (value.divRepaymentTime === undefined || isStrictCalendarDate(value.divRepaymentTime)) &&
    (value.selectedCouponId === undefined ||
      value.selectedCouponId === null ||
      (typeof value.selectedCouponId === 'string' && value.selectedCouponId.trim().length > 0)) &&
    (value.selectedDebtContractNos === undefined ||
      (Array.isArray(value.selectedDebtContractNos) &&
        value.selectedDebtContractNos.every(
          (item) => typeof item === 'string' && item.trim().length > 0
        ))) &&
    // byClaude-start 2026-08-28 10:36:26
    (value.selectedDebtKeys === undefined ||
      (Array.isArray(value.selectedDebtKeys) &&
        value.selectedDebtKeys.every(isDebtSelectionKey))) &&
    // byClaude-end 2026-08-28 10:36:26
    // byClaude-start 2026-08-31 17:49:47
    (value.pendingDebtRecovery === undefined || isPendingDebtRecovery(value.pendingDebtRecovery)) &&
    // byClaude-end 2026-08-31 17:49:47
    (value.clearCardBankStates === undefined ||
      (Array.isArray(value.clearCardBankStates) &&
        value.clearCardBankStates.every(isClearCardBankState))) &&
    (value.clearCardDraftBankStates === undefined ||
      (Array.isArray(value.clearCardDraftBankStates) &&
        value.clearCardDraftBankStates.every(isClearCardBankState))) &&
    (value.clearCardCompVisible === undefined || typeof value.clearCardCompVisible === 'boolean') &&
    (value.lowIncomeData === undefined || isLowIncomeCacheData(value.lowIncomeData)) &&
    (value.isCheckDcSwitch === undefined || typeof value.isCheckDcSwitch === 'boolean')
  );
};

export const readUnionLoanProductCache = (
  value: unknown,
  productType: UnionLoanProductType
): UnionLoanCacheData => {
  if (!isRecord(value)) {
    return {};
  }

  const productCache = value[productType];
  return isValidProductCache(productCache) ? { ...productCache } : {};
};

const unionLoanCacheSlice = createSlice({
  name: 'unionLoanCache',
  initialState,
  reducers: {
    setUnionLoanCache: (
      state,
      action: PayloadAction<{ productType: UnionLoanProductType; data: UnionLoanCacheData }>
    ) => {
      state[action.payload.productType] = action.payload.data;
    },
    clearUnionLoanCache: (state, action: PayloadAction<{ productType: UnionLoanProductType }>) => {
      state[action.payload.productType] = {};
    },
  },
});

export const { setUnionLoanCache, clearUnionLoanCache } = unionLoanCacheSlice.actions;
export default unionLoanCacheSlice.reducer;

// Contains claude-generated edits.
